import styled, { useTheme } from "styled-components"
import PricingCard, { PricingCardProps } from "./PricingCard"
import { theme } from "@/common/Mixin"
import EnvelopTop from "@/common/assets/image/Mainpage/hi/Top.svg"
import NextImg from "../NextImg"
import Collapsible from "../Collapsible"
import { useLayoutEffect, useEffect, useState } from "react"
import React from "react"
import { useMediaQuery } from "@mui/material"

const Fade = require("react-reveal/Fade")

export interface PricingBoardProps {
  id?: string
  description?: string
  slogan?: string
  cards: PricingCardProps[]
}

export default function PricingBoard({
  description,
  slogan,
  cards,
  id
}: PricingBoardProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(`(${theme.lg})`)
  // Start of the code for the dynamic height of the checklists
  // Note the following refs and calculations are for the horizontal alignment of the checklists
  // And it assumes that every card has the same length of detail items
  const compareItemLength = cards.length === 0 ? 0 : cards[0].detail.length
  const [maxHeights, setMaxHeights] = useState(Array(compareItemLength).fill(0))
  const refArray: Array<Array<React.RefObject<HTMLDivElement>>> = Array.from(
    { length: cards.length },
    () => Array.from({ length: compareItemLength }, () => React.createRef())
  )

  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect
  useIsomorphicLayoutEffect(() => {
    const observers: ResizeObserver[] = []

    refArray.forEach((refsForRow, rowIndex) => {
      refsForRow.forEach((ref, columnIndex) => {
        const observer = new ResizeObserver(() => {
          if (ref.current) {
            const newHeight = ref.current.getBoundingClientRect().height

            if (newHeight > maxHeights[columnIndex]) {
              setMaxHeights(prev => {
                const newArr = [...prev]
                newArr[columnIndex] = newHeight
                return newArr
              })
            }
          }
        })
        if (ref.current) {
          observer.observe(ref.current)
        }
        observers.push(observer)
      })
    })

    // Cleanup function for useEffect
    return () => {
      observers.forEach(observer => observer.disconnect())
    }
  }, [cards, maxHeights, refArray])
  // End of the code for the dynamic height of the checklists

  if (cards.length === 0) return null

  return (
    <Wrapper id={id}>
      <TopWrapper>
        <NextImg src={EnvelopTop} />
      </TopWrapper>
      <Collapsible>
        <LayoutWrapper className="kg-wrapper">
          <Fade bottom>
            <TitleWrapper>
              {description && (
                <DescWrapper className="title">{description}</DescWrapper>
              )}
              {slogan && <SloganWrapper>{slogan}</SloganWrapper>}
            </TitleWrapper>
          </Fade>
          <CardsWrapper>
            {cards.map((card, index) => {
              const comp = (
                <PricingCard
                  key={index}
                  {...card}
                  detailsRef={refArray[index]}
                  heights={maxHeights}
                />
              )
              return isMobile ? card.isRecommended && comp : comp
            })}
          </CardsWrapper>
        </LayoutWrapper>
      </Collapsible>
      <BottomWrapper>
        <NextImg src={EnvelopTop} />
      </BottomWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background: ${theme.color.backgroundBlack};
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: center;
  align-self: stretch;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`

const SloganWrapper = styled.h2`
  color: ${theme.color.textContrast};
  text-align: center;

  @media (${theme.lg}) {
    width: 100%;
    text-align: left;
  }
`

const DescWrapper = styled.div`
  background: ${theme.color.primaryLinear};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  align-self: stretch;
  gap: 64px;
  border-radius: 32px;

  @media (${theme.lg}) {
    flex-direction: column;
    gap: 32px;
  }
`

const TopWrapper = styled.div`
  margin-bottom: 3vh;
  width: 100%;
  transform: translateY(-1px);
  @media (${theme.lg}) {
    margin-bottom: 0vh;
  }
`

const BottomWrapper = styled.div`
  margin-top: 3vh;
  width: 100%;
  transform: rotate(180deg) translateY(-1px);
  @media (${theme.lg}) {
    margin-top: 0vh;
  }
`
